import { Capability } from '~/graphql/generated/graphql';

export interface ContentLockedRoute {
  path: string;
  missing: Capability[];
}

/**
 * The admin route the capability gate refused, shared between
 * `02.capability-route-protection` (writer) and the default layout (reader),
 * which swaps the page component for the locked takeover.
 *
 * This is the *committed* lock: it always describes the route the router is actually on. The gate
 * runs before the router moves, so it publishes a pending verdict instead and
 * `00.capabilityLockCommit` promotes it here once `currentRoute` has caught up.
 */
export const useContentLockedRoute = () =>
  useState<ContentLockedRoute | null>('content-locked-route', () => null);

/**
 * The gate's verdict for the navigation in flight, wrapped so that "allowed" (a null lock) is
 * distinguishable from "nothing pending", and keyed by the destination it was reached for so a
 * verdict is only ever committed by the navigation it belongs to.
 */
export interface ContentLockedRouteVerdict {
  /** The path the gate read — `to.path` of the navigation this verdict describes. */
  path: string;
  lock: ContentLockedRoute | null;
}

/**
 * The verdict `02.capability-route-protection` reached for the route being navigated to, held
 * until the router has moved. Clearing the committed lock in `beforeEach` would drop the takeover
 * while the browser is still on the locked path, remounting the locked page — and firing its
 * queries — against a route the viewer is only leaving.
 */
export const usePendingContentLockedRoute = () =>
  useState<ContentLockedRouteVerdict | null>('content-locked-route-pending', () => null);

/** The admin surface, which is the only place a capability denial has a lock or a toast to show. */
export const isContentAdminRoute = (path: string): boolean =>
  path === '/content' || path.startsWith('/content/');

/**
 * A denial's extension carries the capability as its GraphQL enum name (`ADMIN_ADMINS_EDIT`) —
 * the same spelling the generated `Capability` enum uses for its values, so recognising it is a
 * membership check. Anything unrecognised resolves to null and the route's own capabilities are
 * named instead.
 */
const CAPABILITY_VALUES = new Set<string>(Object.values(Capability));

const capabilityFromDenialExtension = (value: unknown): Capability | null =>
  typeof value === 'string' && CAPABILITY_VALUES.has(value) ? (value as Capability) : null;

/**
 * The lock a gated GraphQL denial implies for the route that issued it. A capability the
 * viewer held when the gate ran can be revoked underneath them, and the backend directive
 * is the enforcement — so the page it denies is locked exactly as if the gate had refused
 * the navigation, rather than blowing the admin shell away with a full-page error. A route
 * that declares no capabilities is not a capability denial and keeps the global handling.
 *
 * Only the route's own gate can lock it. A page composes queries from more than one area — a
 * rewards queue reads trust context, a users detail reads rewards cells — and a refusal from one
 * of those is not a statement about the page the viewer is on. So a capability the route does not
 * require resolves to null and the denial stays with the component that asked for it; locking
 * there would take a page away from a viewer who holds it.
 *
 * Among the route's own capabilities the refused one wins over the whole declared list: naming
 * every capability would tell the viewer to request ones they already hold. A denial that names
 * nothing recognisable falls back to the meta — the route is gated and the refusal came from it.
 */
export const contentLockedRouteFromDenial = (
  route: { path: string; meta: { requiredCapabilities?: Capability[] } },
  refusedCapability?: unknown,
): ContentLockedRoute | null => {
  const required = route.meta.requiredCapabilities;
  if (!required?.length) {
    return null;
  }

  const refused = capabilityFromDenialExtension(refusedCapability);
  if (!refused) {
    return { path: route.path, missing: [...required] };
  }

  return required.includes(refused) ? { path: route.path, missing: [refused] } : null;
};

// One toast per capability per page visit: a page issues several queries, and a denial that refuses
// more than one of them would otherwise stack identical toasts on top of each other.
const notifiedCapabilities = new Set<Capability>();

/**
 * Forget what this page visit has already said. Called by the route gate on every navigation, so
 * the dedupe covers one visit rather than one path: leaving a page and coming back to it is a new
 * visit, and its side query is refused again with the same silently empty table to explain.
 */
export const resetCrossAreaDenialNotices = (): void => {
  notifiedCapabilities.clear();
};

/**
 * A capability the route does not require was refused: a side query reaching into another area
 * from a page the viewer legitimately holds. The page keeps rendering — locking it would take it
 * away from someone entitled to it — but the component left holding the denial usually shows
 * nothing at all (an empty table reads as "no records" rather than "not allowed"), so name the
 * missing capability in a toast. Admin-only: elsewhere there is no capability vocabulary to show.
 */
export const notifyCrossAreaDenial = (path: string, refusedCapability?: unknown): void => {
  const capability = capabilityFromDenialExtension(refusedCapability);
  // Client-only: the toast list is module state rather than transferred state, so one raised
  // while rendering on the server would never reach the browser that asked for the page — and
  // would sit in the server's list for whoever asked next.
  if (!import.meta.client || !capability || !isContentAdminRoute(path)) {
    return;
  }

  if (notifiedCapabilities.has(capability)) {
    return;
  }
  notifiedCapabilities.add(capability);

  const { $i18n } = useNuxtApp();
  useToast().show({
    variant: 'error',
    title: $i18n.t('content.locked.title'),
    message: $i18n.t('content.locked.actionRequires', { capability }),
  });
};

/**
 * The whole response to a `MISSING_CAPABILITY` denial, in the order its two halves have to run: the
 * route's own gate first, and the cross-area toast only when that gate declines to lock. A locked
 * route is never also toasted — the takeover already names the capability, so a toast beside it
 * would say the same thing twice. Returns the lock for the caller to publish, or null.
 */
export const resolveCapabilityDenial = (
  route: { path: string; meta: { requiredCapabilities?: Capability[] } },
  refusedCapability?: unknown,
): ContentLockedRoute | null => {
  const lockedRoute = contentLockedRouteFromDenial(route, refusedCapability);

  if (lockedRoute) {
    return lockedRoute;
  }

  notifyCrossAreaDenial(route.path, refusedCapability);

  return null;
};
